<script setup lang="ts">
import { IFactura } from '@shared/types'
import { EStatusFactura } from '@shared/types/enums'
import { formatDate } from '@/utils/dateFormatter'
import { computed, ref, onMounted } from 'vue'
import { ref as storageRef, getDownloadURL } from 'firebase/storage'
import { doc, updateDoc } from 'firebase/firestore'
import { hasAdminRole } from '@/components/utils/functions'
import { useStore } from 'vuex'
import { apiService } from '@/services/apiService'
import BaseModal from '@/components/BaseModal.vue'
import { dbRegistros, storage } from '@/firebase/config'
interface IFacturaWithId extends IFactura {
	id: string
}
const props = defineProps<{
	factura: IFacturaWithId
}>()
defineOptions({
	inheritAttrs: false
})
const emit = defineEmits(['factura-eliminada'])
const store = useStore()
const factura = computed(() => props.factura)
const paymentDateText = computed(() => formatDate(factura.value.dateFactura))

const resolveStoragePath = (rawPath?: string | null): string | null => {
	if (rawPath == null) return null

	const trimmed = rawPath.trim()
	if (!trimmed) return null

	if (/^https?:\/\//i.test(trimmed)) {
		return trimmed
	}

	if (trimmed.startsWith('gs://')) {
		const withoutScheme = trimmed.slice(5)
		const slashIndex = withoutScheme.indexOf('/')
		if (slashIndex === -1) return null
		return withoutScheme.slice(slashIndex + 1).replace(/^\/+/, '')
	}

	return trimmed.replace(/^\/+/, '')
}

const hasComplement = computed(() => Boolean(resolveStoragePath(factura.value.complementPath)))
const isAdmin = ref(false)
const showEditModal = ref(false)
const showConfirmModal = ref(false)
const error = ref<string>('')
const showErrorModal = ref(false)
const showSuccessModal = ref(false)
const successMessage = ref<string>('')
const getFileNameFromPath = (rawPath: string | undefined, fallback: string) => {
	const resolvedPath = resolveStoragePath(rawPath)
	if (!resolvedPath) return fallback

	if (/^https?:\/\//i.test(resolvedPath)) {
		try {
			const pathname = new URL(resolvedPath).pathname
			const lastSegment = pathname.split('/').pop()
			return lastSegment ? decodeURIComponent(lastSegment) : fallback
		} catch {
			return fallback
		}
	}

	return resolvedPath.split('/').pop() ?? fallback
}

const descargarPdf = async () => {
	await downloadFileDirectly(factura.value.pdfPath, getFileNameFromPath(factura.value.pdfPath, 'factura.pdf'))
}

const descargarXml = async () => {
	await downloadFileDirectly(factura.value.xmlPath, getFileNameFromPath(factura.value.xmlPath, 'factura.xml'))
}

const descargarComplemento = async () => {
	await downloadFileDirectly(factura.value.complementPath, getFileNameFromPath(factura.value.complementPath, 'complemento.xml'))
}

const downloadFileDirectly = async (rawPath: string | undefined, fileName = 'archivo-descargado') => {
	const storagePath = resolveStoragePath(rawPath)
	if (!storagePath) {
		error.value = 'No hay archivo disponible para descargar.'
		showErrorModal.value = true
		return
	}

	try {
		const url = /^https?:\/\//i.test(storagePath) ? storagePath : await getDownloadURL(storageRef(storage, storagePath))

		const link = document.createElement('a')
		link.href = url
		link.download = fileName
		link.target = '_blank'
		document.body.appendChild(link)
		link.click()
		link.remove()
	} catch (downloadError) {
		console.error('Error al descargar:', downloadError)
		error.value = 'No se pudo descargar el archivo. Verifica que exista en el almacenamiento.'
		showErrorModal.value = true
	}
}
const openEditModal = () => {
	showEditModal.value = true
}
const editarFactura = async () => {
	const docRef = doc(dbRegistros, 'facturas', factura.value.id)
	const payload: Record<string, any> = {
		status: factura.value.status
	}

	await updateDoc(docRef, payload)
		.then(async () => {
			successMessage.value = 'Factura actualizada correctamente'
			showSuccessModal.value = true
		})
		.catch(() => {
			error.value = 'Error al actualizar la factura'
			showErrorModal.value = true
		})
		.finally(() => {
			showEditModal.value = false
		})
}
const eliminarFactura = async () => {
	showConfirmModal.value = false
	await apiService
		.post('createInvoiceNode/eliminar', {
			id: factura.value.id
		})
		.then(async () => {
			successMessage.value = 'Factura eliminada correctamente'
			showSuccessModal.value = true
		})
		.catch(() => {
			error.value = 'Error al eliminar la factura'
			showErrorModal.value = true
		})
		.finally(() => {
			emit('factura-eliminada')
			showEditModal.value = false
		})
}

onMounted(async () => {
	isAdmin.value = await hasAdminRole(store.state.user.uid)
})
</script>
<template>
	<VaCard class="receipt-card" v-bind="$attrs">
		<VaCardContent class="receipt-card-content">
			<div class="receipt-merchant">
				<div class="receipt-merchant-left">
					<div class="receipt-merchant-text">
						<div class="receipt-merchant-name">{{ factura.title }}</div>
						<div class="receipt-merchant-sub">Fecha : {{ paymentDateText }}</div>
					</div>
				</div>
				<div class="receipt-merchant-right">
					<VaButton icon="picture_as_pdf" color="primary" size="small" class="receipt-action-btn" @click="descargarPdf"> Descargar PDF </VaButton>
					<VaButton icon="code" color="success" size="small" class="receipt-action-btn" @click="descargarXml">Descargar XML</VaButton>
					<VaButton v-if="hasComplement" icon="layers" color="warning" size="small" class="receipt-action-btn" @click="descargarComplemento">Descargar complemento</VaButton>
					<span
						class="receipt-status"
						:class="{
							'is-pagada': factura.status === EStatusFactura.PAGADA,
							'is-pendiente': factura.status === EStatusFactura.PENDIENTE,
							'is-cancelada': factura.status === EStatusFactura.CANCELADA
						}">
						{{ factura.status }}
					</span>
					<VaButton v-if="isAdmin" icon="edit" color="warning" size="small" class="receipt-action-btn" @click="openEditModal">Editar</VaButton>
					<VaButton v-if="isAdmin" icon="delete" color="danger" size="small" class="receipt-action-btn" @click="showConfirmModal = true">Eliminar</VaButton>
				</div>
			</div>
		</VaCardContent>
	</VaCard>
	<BaseModal v-model="showEditModal" title="" message="Editar factura" :test-mode="false" size="medium">
		<template #body>
			<div class="edit-factura">
				<div class="edit-factura__header">
					<div class="edit-factura__header-left">
						<div class="edit-factura__icon">
							<VaIcon name="tune" />
						</div>
						<div class="edit-factura__titles">
							<h3 class="edit-factura__title">Editar estatus</h3>
							<p class="edit-factura__subtitle">Selecciona el nuevo estatus de la factura.</p>
						</div>
					</div>
					<VaBadge
						class="edit-factura__badge"
						:text="factura.status"
						:color="factura.status === EStatusFactura.PAGADA ? 'success' : factura.status === EStatusFactura.CANCELADA ? 'danger' : 'warning'" />
				</div>

				<VaDivider class="edit-factura__divider" />

				<div class="edit-factura__content">
					<VaRadio
						v-model="factura.status"
						class="edit-factura__radio"
						:options="[
							{ text: 'Pendiente', value: EStatusFactura.PENDIENTE },
							{ text: 'Pagada', value: EStatusFactura.PAGADA },
							{ text: 'Cancelada', value: EStatusFactura.CANCELADA }
						]"
						value-by="value"
						text-by="text" />
				</div>
			</div>
		</template>
		<template #footer>
			<div class="edit-factura__footer">
				<VaButton preset="secondary" color="secondary" @click="showEditModal = false">Cancelar</VaButton>
				<VaButton color="primary" @click="editarFactura">Guardar cambios</VaButton>
			</div>
		</template>
	</BaseModal>
	<BaseModal v-model="showErrorModal" title="Error" :message="error" :testMode="false" size="medium">
		<template #body>
			<div class="error-message">
				<div class="error-header">
					<div class="error-icon">
						<i class="fas fa-exclamation-triangle"></i>
					</div>
					<h3 class="error-title">Error</h3>
				</div>
				<p class="error-description">{{ error }}</p>
			</div>
		</template>
		<template #footer>
			<button class="btn btn-primary" @click="showErrorModal = false">Entendido</button>
		</template>
	</BaseModal>
	<BaseModal v-model="showSuccessModal" title="" :message="successMessage" :test-mode="false" size="medium">
		<template #body>
			<div class="success-message">
				<div class="success-header">
					<div class="success-icon">
						<i class="fas fa-check-circle"></i>
					</div>
					<h3 class="success-title">Éxito</h3>
				</div>
				<p class="success-description">{{ successMessage }}</p>
			</div>
		</template>
		<template #footer>
			<button class="btn btn-primary" @click="showSuccessModal = false">Entendido</button>
		</template>
	</BaseModal>
	<BaseModal v-model="showConfirmModal" title="Confirmación" message="¿Estás seguro de querer eliminar esta factura?" :test-mode="false" size="medium">
		<template #body>
			<div class="confirm-message">
				<p class="confirm-message-text">¿Estás seguro de querer eliminar esta factura?</p>
			</div>
		</template>
		<template #footer>
			<button class="btn btn-primary" @click="showConfirmModal = false">Cancelar</button>
			<button class="btn btn-primary" @click="eliminarFactura">Eliminar</button>
		</template>
	</BaseModal>
</template>

<style scoped>
.confirm-message {
	background: linear-gradient(135deg, #e6be7a 0%, #dd9b28 100%);
	border: 2px solid rgb(225, 199, 199);
	border-radius: 16px;
	padding: 1.5rem;
	margin: 1rem 0;
	box-shadow: 0 8px 32px rgba(254, 178, 178, 0.3);
	animation: slideInSuccess 0.4s ease-out;
}
.confirm-message-text {
	font-size: 1.1rem;
	line-height: 1.6;
	margin-bottom: 1.5rem;
	padding: 1rem;
	background: rgba(255, 255, 255, 0.7);
	border-radius: 12px;
	border-left: 4px solid #dd9b28;
}
.error-description {
	font-size: 1.1rem;
	line-height: 1.6;
	margin-bottom: 1.5rem;
	padding: 1rem;
	background: rgba(255, 255, 255, 0.7);
	border-radius: 12px;
	border-left: 4px solid #f71d1d;
}
.error-message {
	background: linear-gradient(135deg, #ecbaba 0%, #e67a7a 100%);
	border: 2px solid #f71d1d;
	border-radius: 16px;
	padding: 1.5rem;
	margin: 1rem 0;
	box-shadow: 0 8px 32px rgba(254, 178, 178, 0.3);
	animation: slideInError 0.4s ease-out;
}

.error-header {
	display: flex;
	align-items: center;
	gap: 1rem;
	margin-bottom: 1.5rem;
	padding-bottom: 1rem;
	border-bottom: 2px solid #f68484;
}

.error-icon {
	width: 48px;
	height: 48px;
	background: linear-gradient(135deg, #e67a7a 0%, #e67a7a 100%);
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 4px 16px rgba(245, 101, 101, 0.4);
}

.error-icon i {
	color: white;
	font-size: 1.5rem;
	animation: pulse 2s infinite;
}

.error-title {
	margin: 0;
	color: #e67a7a;
	font-size: 1.5rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}
:deep(.dp__input) {
	background: var(--va-background-element);
	border: 1px solid var(--va-background-border);
	border-radius: 50px;
}

:deep(.dp__input)::placeholder {
	color: rgba(255, 255, 255, 0.7);
}
.success-description {
	font-size: 1.1rem;
	line-height: 1.6;
	margin-bottom: 1.5rem;
	padding: 1rem;
	background: rgba(255, 255, 255, 0.7);
	border-radius: 12px;
	border-left: 4px solid #28a745;
}
.success-message {
	background: linear-gradient(135deg, #28a745 0%, #28a745 100%);
	border: 2px solid #28a745;
	border-radius: 16px;
	padding: 1.5rem;
	margin: 1rem 0;
	box-shadow: 0 8px 32px rgba(254, 178, 178, 0.3);
	animation: slideInSuccess 0.4s ease-out;
}

.success-header {
	display: flex;
	align-items: center;
	gap: 1rem;
	margin-bottom: 1.5rem;
	padding-bottom: 1rem;
	border-bottom: 2px solid #ffffff;
}

.success-icon {
	width: 48px;
	height: 48px;
	background: linear-gradient(135deg, #28a745 0%, #28a745 100%);
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 4px 16px rgba(245, 101, 101, 0.4);
}

.success-icon i {
	color: white;
	font-size: 1.5rem;
	animation: pulse 2s infinite;
}

.success-title {
	margin: 0;
	color: #ffffff;
	font-size: 1.5rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}

.edit-factura {
	display: flex;
	flex-direction: column;
	gap: 0.85rem;
}

.edit-factura__header {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 0.75rem;
}

.edit-factura__header-left {
	display: flex;
	align-items: flex-start;
	gap: 0.75rem;
}

.edit-factura__icon {
	width: 40px;
	height: 40px;
	border-radius: 12px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: rgba(30, 58, 138, 0.16);
	color: #1e3a8a;
	border: 1px solid rgba(30, 58, 138, 0.22);
}

.edit-factura__titles {
	min-width: 0;
}

.edit-factura__title {
	margin: 0;
	font-size: 1.1rem;
	font-weight: 900;
	color: #0f172a;
	letter-spacing: 0.01em;
}

.edit-factura__subtitle {
	margin: 0.2rem 0 0 0;
	font-size: 0.9rem;
	color: #475569;
}

.edit-factura__badge {
	flex: 0 0 auto;
	text-transform: uppercase;
	letter-spacing: 0.06em;
	font-weight: 900;
}

.edit-factura__divider {
	margin: 0.15rem 0 0.25rem 0;
	opacity: 0.7;
}

.edit-factura__content {
	padding: 0.5rem 0.75rem;
	border-radius: 14px;
	background: linear-gradient(180deg, rgba(248, 250, 252, 0.95) 0%, rgba(255, 255, 255, 0.95) 100%);
	border: 1px solid rgba(148, 163, 184, 0.35);
}

.edit-factura__radio {
	width: 100%;
}

.edit-factura__footer {
	display: flex;
	justify-content: flex-end;
	gap: 0.65rem;
}

.receipt-merchant-right {
	display: flex;
	align-items: center;
	gap: 0.55rem;
	flex: 0 0 auto;
}

.receipt-card {
	border-radius: 12px;
	overflow: hidden;
	box-shadow: 0 6px 16px rgba(17, 24, 39, 0.1);
	margin-bottom: 0.5rem;
}

.receipt-card-content {
	padding: 0.75rem;
}

.receipt-merchant {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 0.75rem;
	padding: 0;
}

.receipt-merchant-left {
	display: flex;
	align-items: center;
	gap: 0.6rem;
	min-width: 0;
}

.receipt-merchant-text {
	min-width: 0;
}

.receipt-merchant-name {
	font-size: 1.02rem;
	font-weight: 800;
	color: #2f3a45;
	line-height: 1.2;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.receipt-merchant-sub {
	margin-top: 0.15rem;
	font-size: 0.8rem;
	color: #718096;
}

.receipt-status {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	padding: 0.25rem 0.55rem;
	border-radius: 999px;
	font-size: 0.72rem;
	font-weight: 850;
	letter-spacing: 0.06em;
	border: 1px solid rgba(148, 163, 184, 0.4);
	background: #f8fafc;
	color: #334155;
	text-transform: uppercase;
	white-space: nowrap;
}

.receipt-status.is-pagada {
	background: rgba(16, 185, 129, 0.12);
	border-color: rgba(16, 185, 129, 0.35);
	color: #064e3b;
}

.receipt-status.is-pendiente {
	background: rgba(245, 158, 11, 0.12);
	border-color: rgba(245, 158, 11, 0.35);
	color: #713f12;
}

.receipt-status.is-cancelada {
	background: rgba(239, 68, 68, 0.1);
	border-color: rgba(239, 68, 68, 0.28);
	color: #701a1a;
}

.receipt-action-btn {
	min-height: 30px;
	padding: 0 10px;
	font-size: 0.85rem;
}
</style>
